import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/axios/api-service';
import { ENDPOINTS } from '../services/endpoints';

export const fetchTicketSummary = createAsyncThunk(
  'report/fetchTicketSummary',
  async (params, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.action_type) queryParams.append('action_type', params.action_type);
      if (params.projects) queryParams.append('projects', params.projects);
      if (params.labels) queryParams.append('labels', params.labels);
      if (params.task_type) queryParams.append('task_type', params.task_type);
      if (params.assignees) queryParams.append('assignees', params.assignees);
      if (params.priorities) queryParams.append('priorities', params.priorities);
      if (params.due_from) queryParams.append('due_from', params.due_from);
      if (params.due_to) queryParams.append('due_to', params.due_to);
      if (params.from_date) queryParams.append('from_date', params.from_date);
      if (params.to_date) queryParams.append('to_date', params.to_date);

      const response = await api.get(`${ENDPOINTS.REPORT_TICKET_SUMMARY}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchOverdueTasks = createAsyncThunk(
  'report/fetchOverdueTasks',
  async (params, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.projects) queryParams.append('projects', params.projects);
      if (params.labels) queryParams.append('labels', params.labels);
      if (params.task_type) queryParams.append('task_type', params.task_type);
      if (params.assignees) queryParams.append('assignees', params.assignees);
      if (params.priorities) queryParams.append('priorities', params.priorities);
      if (params.due_from) queryParams.append('due_from', params.due_from);
      if (params.due_to) queryParams.append('due_to', params.due_to);
      if (params.created_from) queryParams.append('created_from', params.created_from);
      if (params.created_to) queryParams.append('created_to', params.created_to);

      const response = await api.get(`${ENDPOINTS.REPORT_OVERDUE_TASKS}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchTicketsCount = createAsyncThunk(
  'report/fetchTicketsCount',
  async (params, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.projects) queryParams.append('projects', params.projects);
      if (params.labels) queryParams.append('labels', params.labels);
      if (params.task_type) queryParams.append('task_type', params.task_type);
      if (params.assignees) queryParams.append('assignees', params.assignees);
      if (params.priorities) queryParams.append('priorities', params.priorities);
      if (params.month) queryParams.append('month', params.month);
      if (params.year) queryParams.append('year', params.year);

      const response = await api.get(`${ENDPOINTS.REPORT_TICKETS_COUNT}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchTicketsList = createAsyncThunk(
  'report/fetchTicketsList',
  async (params, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.projects) queryParams.append('projects', params.projects);
      if (params.labels) queryParams.append('labels', params.labels);
      if (params.task_type) queryParams.append('task_type', params.task_type);
      if (params.assignees) queryParams.append('assignees', params.assignees);
      if (params.priorities) queryParams.append('priorities', params.priorities);
      if (params.date) queryParams.append('date', params.date);

      const response = await api.get(`${ENDPOINTS.REPORT_TICKETS_LIST}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const reportSlice = createSlice({
  name: 'report',
  initialState: {
    ticketSummary: null,
    overdueTasks: null,
    ticketsCount: null,
    ticketsList: null,
    loading: false,
    overdueLoading: false,
    ticketsCountLoading: false,
    ticketsListLoading: false,
    error: null,
  },
  reducers: {
    clearReportData: (state) => {
      state.ticketSummary = null;
      state.overdueTasks = [];
      state.ticketsCount = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTicketSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTicketSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.ticketSummary = action.payload.data;
      })
      .addCase(fetchTicketSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchOverdueTasks.pending, (state) => {
        state.overdueLoading = true;
        state.error = null;
      })
      .addCase(fetchOverdueTasks.fulfilled, (state, action) => {
        state.overdueLoading = false;
        state.overdueTasks = action.payload.data;
      })
      .addCase(fetchOverdueTasks.rejected, (state, action) => {
        state.overdueLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchTicketsCount.pending, (state) => {
        state.ticketsCountLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketsCount.fulfilled, (state, action) => {
        state.ticketsCountLoading = false;
        state.ticketsCount = action.payload.data;
      })
      .addCase(fetchTicketsCount.rejected, (state, action) => {
        state.ticketsCountLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchTicketsList.pending, (state) => {
        state.ticketsListLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketsList.fulfilled, (state, action) => {
        state.ticketsListLoading = false;
        state.ticketsList = action.payload.data;
      })
      .addCase(fetchTicketsList.rejected, (state, action) => {
        state.ticketsListLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearReportData } = reportSlice.actions;
export default reportSlice.reducer;
